/**
 * Rutas del módulo Piloto — card lRqCSV1P (M3.4).
 *
 * Consolida en un solo lugar el registro de las 8 rutas bajo `/pilots/*`:
 *   - 3 rutas ya en staging (M2.5 F1 wizard, M3.2 dashboard, M3.3 detail).
 *   - 5 placeholders para las cards M4 (F1 histórico, F2 evaluation, F3
 *     committee, lead assignment, nuevo piloto).
 *
 * El router principal (``app/router/index.tsx``) spreadea ``pilotRoutes``
 * dentro del árbol ``ProtectedRoute > MainLayout > children``. Los paths
 * son relativos al layout autenticado.
 *
 * ``RequireAdminPilot`` es un HOC que gata las rutas admin-only
 * (``pilots/new`` y ``pilots/:pilotId/leads/assign``): cualquier usuario sin
 * el permission ``admin:pilot`` en el token Auth0 ve ``PermissionDenied``.
 *
 * Card 64ZjsZ6n — ``pilots/:pilotId/committee`` **salió** de ese gate. No es
 * una relajación de permisos: la pantalla pasó a tener dos vistas (form del
 * comité para staff, read-only para el partner del piloto) y un gate
 * booleano a nivel ruta no puede expresar eso. La decisión vive dentro de
 * ``CommitteeNotePage``. Misma lógica en
 * ``pilots/:pilotId/weekly-report/current``, que nunca estuvo gateada.
 */
import { Suspense } from 'react';
import { Link, type RouteObject } from 'react-router-dom';

import LoadingSpinner from '@/components/common/LoadingSpinner';
import { PageSkeleton } from '@/components/common/PageSkeleton';
import { usePermissions } from '@/hooks/usePermissions';
import {
  CommitteeNotePage,
  LeadEvaluationPage,
  LeadsAssignmentPage,
  MyPendingEvaluationsPage,
  NewPilotWizardPage,
  PilotDetailPage,
  PilotsListPage,
  WeeklyReportWizardPage,
} from '@/app/router/lazy-routes';

// =============================================================================
// RequireAdminPilot — HOC RBAC gate.
// =============================================================================

interface RequireAdminPilotProps {
  children: React.ReactNode;
  /**
   * Ruta a la que redirigir el CTA del feedback cuando el usuario NO tiene
   * ``admin:pilot``. Default: ``/pilots`` (la landing del módulo).
   */
  redirectTo?: string;
}

/**
 * Pantalla de feedback visible cuando ``RequireAdminPilot`` deniega el acceso.
 *
 * Reemplaza el ``<Navigate>`` silencioso que teníamos antes — el redirect
 * mudo confundía a usuarios con permission válido pero token aún cargando, y
 * en el caso genuino de "no tenés permiso" tampoco daba feedback claro.
 *
 * No es un componente reutilizado: está inline porque el gate es específico
 * de las rutas del módulo Piloto. Si otro feature necesita el mismo patrón,
 * extraerlo a ``components/common/PermissionDenied.tsx``.
 */
function PermissionDenied({ redirectTo }: { redirectTo: string }) {
  return (
    <div
      className="mx-auto max-w-lg p-8 text-center"
      data-testid="pilots-permission-denied"
      role="alert"
    >
      <h1 className="text-xl font-semibold text-gray-900">
        No tenés permiso para acceder
      </h1>
      <p className="mt-3 text-sm text-gray-600">
        Requerís el permission <code>admin:pilot</code> para entrar a esta
        pantalla. Contactá al administrador de tu company para que te lo
        asigne.
      </p>
      <Link
        to={redirectTo}
        className="mt-6 inline-block rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
      >
        Volver a Pilotos
      </Link>
    </div>
  );
}

/**
 * Wrapper que sólo renderiza sus children si el usuario tiene el permission
 * ``admin:pilot`` en el token Auth0.
 *
 * Race condition histórica (fix 2026-09-18 — bug diagnosticado por OPS en
 * card mztNXRf9): antes usábamos sólo ``hasPermission`` y navegábamos con
 * ``<Navigate>`` en el primer render. Como ``usePermissions`` empieza con
 * ``permissions=[]`` y sólo resuelve el token en el ``useEffect`` inicial,
 * el HOC redirigía SIEMPRE — incluso cuando el user tenía el permission en
 * el JWT. Ahora consumimos ``isLoading`` y mostramos un loader mientras
 * carga; cuando resuelve, o renderizamos children o mostramos feedback
 * visible (``PermissionDenied``).
 */
export function RequireAdminPilot({
  children,
  redirectTo = '/pilots',
}: RequireAdminPilotProps) {
  const { hasPermission, isLoading } = usePermissions();

  if (isLoading) {
    return (
      <div
        className="flex h-64 items-center justify-center"
        data-testid="pilots-permission-loading"
      >
        <LoadingSpinner text="Cargando permisos..." />
      </div>
    );
  }

  if (!hasPermission('admin:pilot')) {
    return <PermissionDenied redirectTo={redirectTo} />;
  }

  return <>{children}</>;
}

// =============================================================================
// ComingSoon — placeholder inline para las rutas M4.
// =============================================================================

interface ComingSoonProps {
  /**
   * Etiqueta principal ("F2 lead evaluation (M4.1)"). Se muestra como h1
   * para que el humano encuentre el TODO sin leer el código.
   */
  label: string;
}

/**
 * Placeholder de rutas cuya page real la implementa una card M4. Se monta
 * inline (mismo bundle que ``routes.tsx``) — no vale la pena crear 5
 * lazy imports idénticos para un componente estático.
 */
export function ComingSoon({ label }: ComingSoonProps) {
  return (
    <div className="p-8" data-testid="pilots-coming-soon">
      <h1 className="text-xl font-semibold text-gray-900">{label}</h1>
      <p className="text-sm text-gray-500 mt-2">
        Esta pantalla se implementa en la siguiente card.
      </p>
    </div>
  );
}

// =============================================================================
// pilotRoutes — array de RouteObject para el router principal.
// =============================================================================

/**
 * Wrapper con ``<Suspense>`` para las páginas lazy del módulo. El fallback
 * es consistente con el resto del router (``<PageSkeleton />``).
 */
function withSuspense(node: React.ReactNode) {
  return <Suspense fallback={<PageSkeleton />}>{node}</Suspense>;
}

/**
 * Array de rutas ``/pilots/*``. El router principal las spreadea dentro de
 * ``ProtectedRoute > MainLayout > children`` — paths son relativos.
 *
 * Orden: list first (landing), luego admin-only ``new``, después las rutas
 * bajo ``:pilotId``. React Router matchea por especificidad, así que el
 * orden textual no cambia el matching, pero facilita la lectura humana.
 */
export const pilotRoutes: RouteObject[] = [
  // Landing del módulo (card f7661DJi — M3.2).
  {
    path: 'pilots',
    element: withSuspense(<PilotsListPage />),
  },
  // Crear piloto (M4.4 — card mztNXRf9). Gate ``admin:pilot``.
  {
    path: 'pilots/new',
    element: (
      <RequireAdminPilot>{withSuspense(<NewPilotWizardPage />)}</RequireAdminPilot>
    ),
  },
  // Detail page (card jG2r7wcx — M3.3).
  {
    path: 'pilots/:pilotId',
    element: withSuspense(<PilotDetailPage />),
  },
  // F1 semanal wizard (card bfkw44PD — M2.5).
  {
    path: 'pilots/:pilotId/weekly-report/current',
    element: withSuspense(<WeeklyReportWizardPage />),
  },
  // Histórico weekly (placeholder — sin card M4 asignada aún).
  {
    path: 'pilots/:pilotId/weekly/:weekNumber',
    element: <ComingSoon label="Histórico weekly report" />,
  },
  // Lead inbox — F2 evaluaciones pendientes del asesor (M4.1).
  // La card M4.3 (`XeUTytyd`) montó la vista admin/PILOT_LEAD en la ruta
  // hermana `pilots/:pilotId/leads/assign`.
  {
    path: 'pilots/:pilotId/leads',
    element: withSuspense(<MyPendingEvaluationsPage />),
  },
  // Lead assignment admin view — M4.3 (card XeUTytyd). Gate `admin:pilot`.
  // Se monta ANTES que `leads/:leadId/evaluation` porque React Router matchea
  // rutas anidadas por especificidad — `assign` es un segmento fijo que no
  // colisiona con `:leadId/evaluation`, pero conviene mantener juntas las
  // rutas admin del recurso.
  {
    path: 'pilots/:pilotId/leads/assign',
    element: (
      <RequireAdminPilot>
        {withSuspense(<LeadsAssignmentPage />)}
      </RequireAdminPilot>
    ),
  },
  // F2 lead evaluation page (card OX7FNB1q — M4.1).
  {
    path: 'pilots/:pilotId/leads/:leadId/evaluation',
    element: withSuspense(<LeadEvaluationPage />),
  },
  // F3 committee note (card Rklj0Tsw — M4.2).
  //
  // **Sin** `RequireAdminPilot` desde la card 64ZjsZ6n: la ruta tiene dos
  // vistas legítimas, el formulario del comité para staff (`admin:pilot`) y
  // la vista read-only para el partner que participa del piloto. Un gate a
  // nivel ruta sólo puede decir sí/no, así que la decisión vive dentro de
  // `CommitteeNotePage`, que es quien puede renderizar una u otra. El backend
  // sigue siendo la autorización real: el POST exige `admin:pilot` y el GET
  // exige ser miembro de la company del piloto.
  {
    path: 'pilots/:pilotId/committee',
    element: withSuspense(<CommitteeNotePage />),
  },
];
